import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Modal,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { NftInclude, NftStatus } from '@mogul/api-client';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';

import { nftsApi } from '../../../api';
import { useAuth } from '../../../auth';
import { BackHeader } from '../../../components/common/backheader/BackHeader.component';
import {
  Button,
  Colors,
} from '../../../components/legacy/button/Button.component';
import { useNFTContract } from '../../../hooks/nft-contract.hooks';
import { useNFTErc721Contract } from '../../../hooks/nft-erc-721-contract.hooks';
import styles from './NftMinting.module.css';

interface Params {
  nftContractAddress: string;
  nftId: string;
}

export const NftMintingContainer = () => {
  // note type any is a work around for metadata typing, it is listed as String but is actually Object type when
  // returned and causes compilation errors if typed as NftData
  const { nftContractAddress, nftId } = useParams<Params>();
  const [nftData, setNftData] = useState<any>();
  const [openModal, setOpenModal] = useState(false);
  const [recipientAddr, setRecipientAddr] = useState('');
  const [amount, setAmount] = useState('1');
  const [loading, setLoading] = useState(false);
  const [mintingMessage, setMintingMessage] = useState('');
  const [minted, setMinted] = useState(false);
  const { mintNft, walletInfo } = useNFTContract({ nftContractAddress });
  const { mintNft: mintNft721, isErc721 } = useNFTErc721Contract({
    nftContractAddress,
  });

  const [mediaInlineStyle, setMediaInlineStyle] = useState(
    `${styles.mediaBackground} w-full flex justify-center items-center flex-wrap`,
  );

  const { user } = useAuth();
  const history = useHistory();

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const goToCreatorDashBoard = () => {
    history.push('/platform/creators/dashboard');
  };

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      goToCreatorDashBoard();
    } else {
      void nftsApi
        .getNft({
          contractAddress: nftContractAddress,
          id: nftId,
          include: [NftInclude.CreatedBy],
        })
        .then(results => {
          const newNftData = results.data.data as any;

          if (newNftData.chainId === 'BSCTEST') {
            newNftData.chainId = 'BSCTEST';
          }

          if (newNftData.status === NftStatus.Minted) {
            setMinted(true);
            goToCreatorDashBoard();
          }

          if (newNftData?.animation) {
            setMediaInlineStyle(
              `${styles.mediaBackground} w-full flex justify-center items-start flex-wrap`,
            );
          }

          setNftData(newNftData);
        });
      setRecipientAddr(walletInfo.address ?? '');
    }
  }, [walletInfo, user]);

  const submitPreflightCheck = () => {
    return recipientAddr && amount;
  };

  const handleMintNft = async () => {
    if (minted) {
      setMintingMessage(`NFT Is Already Minted On ${nftData.chainId}`);
    } else if (nftData.chainId !== walletInfo.chain.type) {
      setMintingMessage(`NFT Must Be Minted On ${nftData.chainId}`);
    } else {
      setLoading(true);
      let didMint;
      try {
        if (await isErc721()) {
          didMint = await mintNft721(recipientAddr, nftId, nftData.uri);
        } else {
          didMint = await mintNft(
            recipientAddr,
            nftId,
            amount,
            nftData.uri,
            '0x',
          );
        }
        if (didMint) {
          setMinted(true);
          setMintingMessage('Minting Completed');
          void nftsApi.updateNft({
            contractAddress: nftData.contractAddress,
            id: nftData.id,
            nftUpdateData: {
              status: NftStatus.Minted,
              totalSupply: parseInt(amount, 10),
            },
          });
        } else {
          setMintingMessage('Minting Failed');
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={`${styles.nftMintingPageContainer}`}>
      <Box className="lg:pl-14 pt-5">
        <BackHeader
          title="back to Creators Dashboard"
          headerOnPress={goToCreatorDashBoard}
        />
      </Box>
      <Box className={mediaInlineStyle}>
        {nftData?.animation ? (
          <div className="flex flex-col justify-center items-center flex-wrap pb-10">
            <img
              className={'ml-10 mr-10 mt-8 mb-12'}
              height="auto"
              width="448"
              src={nftData.image.publicUrl}
              alt="nft"
            />
            {nftData.animation.publicUrl.slice(
              nftData.animation.publicUrl.length - 3,
              nftData.animation.publicUrl.length,
            ) === 'gif' ? (
              <img
                className={'ml-10 mr-10 mt-8'}
                height="auto"
                width="448"
                src={nftData.animation.publicUrl}
                alt="nftGif"
              />
            ) : (
              <video width="448" height="auto" controls>
                <source src={nftData.animation.publicUrl} type="video/mp4" />
              </video>
            )}
          </div>
        ) : nftData?.image ? (
          <img
            className=" ml-10 mr-10 my-12"
            height="auto"
            width="448"
            src={nftData.image.publicUrl}
            alt="nft"
          />
        ) : (
          <Skeleton
            variant="rect"
            className="px-20 mt-12 mb-14"
            width="448px"
            height="600px"
          />
        )}
        <Box
          className={`${styles.nftInfoContainer} w-1/2 mx-12 mt-24 mb-12 rounded-md`}
        >
          <div className={'text-2xl font-black mb-1 mt-4 mb-3 ml-5 opacity-80'}>
            NFT Info
          </div>
          <div className={'opacity-80'}>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Title</div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.generatedMetadata
                  ? nftData.generatedMetadata.name
                  : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Description</div>
              <div className={'ml-10 break-word pr-4'}>
                {nftData?.generatedMetadata
                  ? nftData.generatedMetadata.description
                  : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>
                Contract Address
              </div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.contractAddress ? nftData.contractAddress : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-3 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Token ID</div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.id ? nftData.id : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Chain ID</div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.chainId ? nftData.chainId : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto min-h-10 flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Updated At</div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.updatedAt
                  ? nftData.createdAt
                      .replaceAll(/[TZ]/g, ' ')
                      .replace('.000', '')
                  : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
            <div
              className={
                'text-lg h-auto flex ml-5 my-2 overflow-auto items-center'
              }
            >
              <div className={`${styles.labels} font-bold`}>Created By</div>
              <div className={'ml-10 break-all pr-4'}>
                {nftData?.createdBy.name ? nftData.createdBy.name : 'null'}
              </div>
            </div>
            <div className={`${styles.lineBreak}`} />
          </div>
          <Button
            color={Colors.COLOR_SCHEME_2}
            className={`${styles.button} opacity-100 font-bold py-3 mt-2 mb-3 ml-auto mr-3`}
            onClick={handleOpen}
          >
            Mint
          </Button>
        </Box>
      </Box>
      <Modal
        open={openModal}
        onClose={handleClose}
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModal}>
          <Box
            className={`${styles.mintModalContainer} w-1/5 flex flex-col items-center`}
          >
            <div
              className={`${styles.modalTitle} text-3xl font-bold text-center`}
            >
              Minting
            </div>
            <Box
              className={`${styles.mintModalSubContainer} pt-8 w-full h-auto flex flex-col items-center rounded-md`}
            >
              {loading && (
                <CircularProgress
                  className="w-3/5"
                  size={16}
                  variant="indeterminate"
                />
              )}
              {!loading && (
                <p className={`${styles.mintMessage} mb-2`}>{mintingMessage}</p>
              )}
              <div className={`${styles.modalInputContainer} mx-1 my-1`}>
                <p className={`${styles.modalLabels}`}>Contract Address</p>
                <input
                  type={'text'}
                  value={nftContractAddress}
                  className={`${styles.modalInput}`}
                  placeholder={'Contract Address'}
                  disabled={true}
                />
              </div>

              <div className={`${styles.modalInputContainer} mx-1 my-1`}>
                <p className={`${styles.modalLabels}`}>Token ID</p>
                <input
                  type={'text'}
                  value={nftId}
                  className={`${styles.modalInput}`}
                  placeholder={'TokenID'}
                />
              </div>

              <div className={`${styles.modalInputContainer} mx-1 my-1`}>
                <p className={`${styles.modalLabels}`}>Recipient Address</p>
                <input
                  type={'text'}
                  value={recipientAddr}
                  className={`${styles.modalInput}`}
                  placeholder={'Recipient Address'}
                  onChange={e => setRecipientAddr(e.target.value)}
                />
              </div>

              <div className={`${styles.modalInputContainer} mx-1 my-1`}>
                <p className={`${styles.modalLabels}`}>Amount</p>
                <input
                  type={'number'}
                  value={amount}
                  className={`${styles.modalInput}`}
                  placeholder={'Amount'}
                  min="0"
                  onChange={e => setAmount(e.target.value)}
                />
              </div>

              <br />
              <button
                className={
                  submitPreflightCheck()
                    ? `${styles.submitButton} mb-8`
                    : `${styles.disabledButton} mb-8`
                }
                onClick={handleMintNft}
              >
                Mint
              </button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};
